import { changeSuspension, fetchReferralUser } from "api/admin/referrals";
import Table from "components/ui/Table";
import useAdminAccess from "hooks/useAdminAccess";
import { useState } from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { RecordTable } from "./Records";
import {
  ActionForm,
  Button,
  Card,
  ErrorState,
  LoadingRows,
  ProgressView,
  UserLink,
  formatDate,
  formatLabel,
  money,
  queryOptions,
  useReferralKey,
} from "./shared";
export default function Participant({ userId }: { userId: number }) {
  const key = useReferralKey();
  const { can } = useAdminAccess();
  const validId = Number.isSafeInteger(userId) && userId > 0;
  const query = useQuery(
    [...key, "user", userId],
    () => fetchReferralUser(userId),
    { ...queryOptions, enabled: validId },
  );
  const [action, setAction] = useState<"suspend" | "reinstate">(),
    [message, setMessage] = useState("");
  if (!validId) return <p role="alert">Enter a valid user ID.</p>;
  if (query.isLoading) return <LoadingRows />;
  if (query.isError || !query.data)
    return (
      <ErrorState error={query.error} retry={() => void query.refetch()} />
    );
  const view = query.data;
  return (
    <div className="space-y-5">
      <Link className="text-sm underline" to="/referrals/records">
        Back to referral records
      </Link>
      <Card
        title={`${view.user.userName || view.user.firstName || "Participant"} · User #${userId}`}
        description={`${view.user.country ?? "Unknown country"} · ${view.user.market}`}
      >
        <div className="p-5 space-y-4 text-sm">
          <p>
            Lifetime qualification:{" "}
            {view.eligibility.eligibleAt
              ? formatDate(view.eligibility.eligibleAt)
              : "Not yet qualified"}{" "}
            · Market: {view.eligibility.marketPaused ? "Paused" : "Enabled"}
          </p>
          <ProgressView
            progress={view.eligibility.progress}
            currency={view.user.market}
          />
          <p className="break-all">
            Referral code: {view.referralCode || "—"}
            <br />
            Referral link: {view.referralLink || "—"}
          </p>
          {view.suspension ? (
            <p className="p-3 rounded bg-red-50">
              Suspended {formatDate(view.suspension.suspendedAt)} by admin #
              {view.suspension.suspendedBy ?? "—"}. Reason:{" "}
              {view.suspension.reason || "—"}
            </p>
          ) : (
            <p>Participation is not suspended.</p>
          )}
          {can("referrals.suspensions.edit") && (
            <Button
              variant={view.suspension ? "secondary" : "danger"}
              onClick={() => {
                setAction(view.suspension ? "reinstate" : "suspend");
                setMessage("");
              }}
            >
              {view.suspension
                ? "Reinstate participant"
                : "Suspend participant"}
            </Button>
          )}
        </div>
      </Card>
      {message && (
        <p role="status" className="text-green-700">
          {message}
        </p>
      )}
      {action && (
        <ActionForm
          key={`${userId}-${action}`}
          title={`${action === "suspend" ? "Suspend" : "Reinstate"} user #${userId}`}
          description={
            action === "suspend"
              ? "Stops referral sharing, reminders, reward credits and referral withdrawals. Existing eligibility and balances are retained."
              : "Restores participation. Normal market, eligibility and cycle limits still apply."
          }
          allowed={can("referrals.suspensions.edit")}
          cancel={() => setAction(undefined)}
          done={(text) => {
            setMessage(text);
            setAction(undefined);
          }}
          run={async (reason) => {
            const result = await changeSuspension(userId, action, reason);
            return result.suspended
              ? "Participant suspended."
              : "Participant reinstated.";
          }}
        />
      )}
      <div className="grid gap-5 sm:grid-cols-2">
        <Card title="Referral balance">
          <div className="p-5 space-y-2 text-sm">
            <p className="text-2xl">
              {money(view.balance.balanceMinor, view.balance.currency)}
            </p>
            <p>
              Lifetime earned:{" "}
              {money(view.balance.lifetimeEarnedMinor, view.balance.currency)}
            </p>
          </div>
        </Card>
        <Card title="Current cycle">
          <div className="p-5 space-y-2 text-sm">
            <p>
              {view.cycle.active
                ? `${formatDate(view.cycle.startAt)} – ${formatDate(view.cycle.endAt)}`
                : "No active cycle"}
            </p>
            <p>
              {view.cycle.rewardsUsed}/{view.cycle.maxRewards} rewards ·{" "}
              {money(view.cycle.amountEarnedMinor, view.cycle.currency)} /{" "}
              {money(view.cycle.amountCapMinor, view.cycle.currency)}
            </p>
          </div>
        </Card>
      </div>
      <Card title="Referrals made">
        <div className="p-5">
          <p className="mb-4 text-sm">
            {Object.entries(view.referralCounts)
              .map(([status, count]) => `${formatLabel(status)}: ${count}`)
              .join(" · ")}
          </p>
          <RecordTable records={view.referrals.results} />
          <Link
            className="inline-block mt-4 text-sm underline"
            to={`/referrals/records?referrer=${userId}`}
          >
            View all referrals by this user
          </Link>
        </div>
      </Card>
      {view.referredBy && (
        <Card title="Referred by">
          <div className="p-5 text-sm">
            <UserLink id={view.referredBy.referrerId} /> · Referral #
            {view.referredBy.id} · {formatLabel(view.referredBy.status)}
          </div>
        </Card>
      )}
      <Card
        title="Recent balance history"
        description="Latest page returned by the participant endpoint."
      >
        <div className="overflow-x-auto">
          <Table
            header={[
              "Date",
              "Movement",
              "Amount",
              "Balance after",
              "Wallet lien",
            ]}
            emptyMessage="No balance movements."
            data={view.history.results.map((item) => [
              formatDate(item.createdAt),
              formatLabel(item.kind),
              money(item.amountMinor, item.currency),
              money(item.balanceAfterMinor, item.currency),
              money(item.walletLienMinor, item.currency),
              undefined,
            ])}
          />
        </div>
        {view.history.meta.hasNextPage && (
          <p className="p-5 text-sm text-gray-500">
            Older balance movements are not included in this view.
          </p>
        )}
      </Card>
    </div>
  );
}
